import * as React from "react";
import { WizardCruisePWAProps } from "./typings";
import { observer } from "mobx-react";
import { LobTitle } from "src/components/shared/LobTitle/LobTitle";
import { SubmitRow } from "src/components/shared/StorefrontWizard/SubmitRow/SubmitRow";
import { LobSubtitle } from "src/components/shared/LobSubtitle/LobSubtitle";
import {
  UitkLayoutGrid,
  UitkLayoutConditionalGridSpan,
  UitkLayoutConditionalGridColumnRow,
} from "uitk-react-layout-grid";
import { CruiseCommon3PPSelect } from "./components/CruiseCommon3PPSelect";
import { Dates } from "src/components/shared/Dates/Dates";
import { useLocalization } from "@shared-ui/localization-context";

export const WizardCruisePWA3PPView: React.FunctionComponent<WizardCruisePWAProps> = observer(
  ({ flexComponent, wizardState, context }) => {
    const { formatText } = useLocalization();
    const [startId, setStartId] = React.useState<string | undefined>("departure-date");

    /**
     * This hook is reached for rehydratation, so if JS is enabled there is no need of keep setting IDs to elements
     * so value is changed to 'undefined' and so it is avoided, if JS is disabled then this hook is never executed
     */
    React.useEffect(() => {
      setStartId(undefined);
    }, []);

    const {
      cruiseWizardState,
      cruiseWizardState: { config: cruiseConfig },
    } = wizardState;

    const mainGridColumns: UitkLayoutConditionalGridColumnRow = { small: 1, medium: 6 };

    const destinationDatesColSpan: UitkLayoutConditionalGridSpan = {
      small: 1,
      medium: 3,
    };

    const portDurLineColSpan: UitkLayoutConditionalGridSpan = {
      small: 1,
      medium: 2,
    };

    const { wizard3pp } = cruiseConfig;
    const { labelLocKeys, queryParam, destinations, departurePorts, cruiseLines, durations, actionUrl } = wizard3pp!;

    const changeDestination = (destination: string) => {
      cruiseWizardState.updateDestination3pp(destination);
    };

    const changeDeparture = (departure: string) => {
      cruiseWizardState.updateDeparturePort3pp(departure);
    };

    const changeDuration = (duration: string) => {
      cruiseWizardState.updateDuration3pp(duration);
    };

    const changeCruiseLine = (cruiseLine: string) => {
      cruiseWizardState.updateCruiseLine3pp(cruiseLine);
    };

    const siteId = context!.site.id;
    const siteIdsWithInputs = [25];

    return (
      <form
        noValidate={true}
        action={actionUrl}
        autoComplete="off"
        className="WizardCruise3ppPWA"
        id={cruiseConfig.elementId}
        data-fm={flexComponent.fmId}
        data-fm-title-id={flexComponent.fmTitleId}
        data-testid="wizard-cruise-3pp"
        onSubmit={cruiseWizardState.submit3pp}
      >
        <LobTitle lobState={cruiseWizardState} />
        {cruiseWizardState.config.heading.visibleLobSubtitle && (
          <LobSubtitle lobState={cruiseWizardState} className={"all-b-padding-four all-t-margin-one"} />
        )}

        {
          // Fix values in AU to make the search
          siteIdsWithInputs.includes(siteId) && (
            <>
              <input type="hidden" name="cobrand" value="36087" />
              <input type="hidden" name="webpagerecno" value="5412" />
              <input type="hidden" name="sort" value="4" />
              <input type="hidden" name="bs" value="1" />
            </>
          )
        }
        <UitkLayoutGrid columns={mainGridColumns} space="three" className="all-t-margin-three">
          <>
            <CruiseCommon3PPSelect
              colSpan={destinationDatesColSpan}
              icon="place"
              placeholder={formatText(labelLocKeys.destination)}
              invalidMessage={formatText(cruiseWizardState.destination3ppInvalidKey)}
              id="cruise-3pp-destination"
              items={destinations}
              param={queryParam.destination}
              currentValue={cruiseWizardState.destination3pp}
              onChange={changeDestination}
            />

            <Dates startId={startId} lobState={cruiseWizardState} colSpan={destinationDatesColSpan} singleDate={true} />

            <CruiseCommon3PPSelect
              colSpan={portDurLineColSpan}
              icon="place"
              placeholder={formatText(labelLocKeys.departure)}
              invalidMessage={formatText(cruiseWizardState.departurePort3ppInvalidKey)}
              id="cruise-3pp-departure"
              items={departurePorts}
              param={queryParam.departure}
              currentValue={cruiseWizardState.departurePort3pp}
              onChange={changeDeparture}
            />

            <CruiseCommon3PPSelect
              colSpan={portDurLineColSpan}
              icon="access_time"
              placeholder={formatText(labelLocKeys.duration)}
              invalidMessage={formatText(cruiseWizardState.duration3ppInvalidKey)}
              id="cruise-3pp-duration"
              items={durations}
              param={queryParam.duration}
              currentValue={cruiseWizardState.duration3pp}
              onChange={changeDuration}
            />

            <CruiseCommon3PPSelect
              colSpan={portDurLineColSpan}
              icon="lob_cruise"
              placeholder={formatText(labelLocKeys.cruiseLine)}
              invalidMessage={formatText(cruiseWizardState.cruiseLine3ppInvalidKey)}
              id="cruise-3pp-cruiseline"
              items={cruiseLines}
              param={queryParam.cruiseLine}
              currentValue={cruiseWizardState.cruiseLine3pp}
              onChange={changeCruiseLine}
            />
          </>
        </UitkLayoutGrid>

        <SubmitRow lobState={cruiseWizardState} rfrr={"SearchClick_Cruises"} />
      </form>
    );
  }
);

export default WizardCruisePWA3PPView;
